import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import { ApplicationSettings, BooleanStrings } from '../constants';
import { UserPrinterDocument } from '../../user-admin';
import { ApplicationSettingService } from './application-setting.service';
import { DocumentPipelineService } from './document-pipeline.service';
import { PrintService } from './print.service';

/** What to print, and where. */
export interface DocumentPrintRequest {
  /** A PipelineDocumentTypes value. */
  documentType: string;
  /** Every entity to print — the server renders them as one paged PDF. */
  reportIds: string[];
  /** The user's configured printer for this document (QZ Tray or PrintNode). */
  printer: UserPrinterDocument;
  copies?: number;
  title?: string;
  /**
   * The legacy client-rendered print for this call site, used only when the
   * UseServerRenderedLabels setting is switched off. Keeping it lets a site
   * roll back instantly without a redeploy; it goes away with the legacy
   * templates once the server path is signed off.
   */
  legacyPrint?: () => Observable<unknown>;
}

/**
 * Prints documents and labels from the ONE server-rendered source.
 *
 * The pipeline renders the PDF; this service hands it to whichever printer the
 * user has configured. QZ Tray and PrintNode therefore print byte-identical
 * output, and no document is ever assembled in the browser.
 *
 * Batching matters: a render costs about the same whatever the page count, so
 * always pass every id in one call rather than looping.
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentPrintService {
  private readonly documentPipelineService = inject(DocumentPipelineService);
  private readonly printService = inject(PrintService);
  private readonly applicationSettingService = inject(ApplicationSettingService);

  private useServerRendered$: Observable<boolean>;

  print(request: DocumentPrintRequest): Observable<unknown> {
    if (!request.reportIds?.length || !request.printer) {
      return of(null);
    }

    return this.useServerRenderedLabels().pipe(
      switchMap((useServerRendered: boolean) => {
        if (!useServerRendered && request.legacyPrint) {
          return request.legacyPrint();
        }
        return this.printFromPipeline(request);
      }),
    );
  }

  private printFromPipeline(request: DocumentPrintRequest): Observable<unknown> {
    const printer = request.printer;
    const batch = {
      documentType: request.documentType,
      reportIds: request.reportIds,
      documentSize: printer.documentSize,
      pageWidthInches: this.toInches(printer.documentWidth, printer.documentUnits),
      pageHeightInches: this.toInches(printer.documentHeight, printer.documentUnits),
      copies: request.copies ?? 1,
      title: request.title,
    };

    // PrintNode: the server renders and submits, so the PDF never travels to
    // the browser. QZ Tray prints locally, so fetch the PDF and hand it over.
    if (printer.printMethod === 'PrintNode' && printer.printNodeId) {
      return this.documentPipelineService.printBatch({ ...batch, printerId: printer.printNodeId });
    }

    return this.documentPipelineService.downloadBatch(batch).pipe(
      switchMap((blob: Blob) => this.toBase64(blob)),
      switchMap((base64: string) => this.printService.printPdfDocument(base64, printer, request.copies ?? 1)),
    );
  }

  /** Cached: one lookup per session, defaulting to the server path. */
  private useServerRenderedLabels(): Observable<boolean> {
    this.useServerRendered$ ??= this.applicationSettingService.get(ApplicationSettings.useServerRenderedLabels).pipe(
      // Only an explicit "No" switches the server path off. This reads the raw
      // value rather than getBoolean because getBoolean maps a MISSING setting
      // to false, which would silently force every site that has never created
      // the row back onto the legacy client-rendered labels.
      map((value: string) => (value ?? '').trim().toUpperCase() !== BooleanStrings.no.toUpperCase()),
      catchError(() => of(true)),
      shareReplay(1),
    );

    return this.useServerRendered$;
  }

  private toBase64(blob: Blob): Observable<string> {
    return new Observable<string>((subscriber) => {
      const reader = new FileReader();
      reader.onerror = () => subscriber.error(reader.error);
      reader.onload = () => {
        // Strip the "data:application/pdf;base64," prefix — printers want the
        // payload only.
        const result = reader.result as string;
        subscriber.next(result.substring(result.indexOf(',') + 1));
        subscriber.complete();
      };
      reader.readAsDataURL(blob);
    });
  }

  private toInches(value: number, units: string): number {
    if (!value || value <= 0) {
      return 0;
    }
    switch ((units ?? '').toUpperCase()) {
      case 'MM':
        return value / 25.4;
      case 'CM':
        return value / 2.54;
      default:
        return value;
    }
  }
}
